import * as React from 'react'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/next'
import * as consent from '@owl-nest/consent'
import * as env from '@owl-nest/config'
import * as models from '@owl-nest/models'
import * as shadow from '@owl-nest/shadow'
import { t, tnc } from '@owl-nest/localize'
import * as dateUtils from '@owl-nest/date'
import * as hooks from '@owl-nest/hooks'
import * as service from '@owl-nest/services'
import * as sharing from '@owl-nest/sharing'

export type NewsContentProps = {
  className?: string
  isEditMode: boolean
  project: api.Project
  news: api.News
  lockedCTA: React.ReactElement
}

export function NewsContent({ className, isEditMode, project, news, lockedCTA }: NewsContentProps) {
  const userEnv = env.useUserEnv()

  const newsContent = news.content && models.i18n.getWithLang(news.content, userEnv.lang, project.lang)
  const newsPreview = models.i18n.getWithLang(news.preview, userEnv.lang, project.lang)
  const newsTitle = models.i18n.getWithLang(news.title, userEnv.lang, project.lang)
  const newsVideo = news.video && models.i18n.getWithLang(news.video, userEnv.lang, project.lang)
  const newsImage = news.image && models.i18n.getWithLang(news.image, userEnv.lang, project.lang)

  // news.contentType can't be used because it's related to the lang
  // whereas we want to mix translated contents here
  const localNewsContentType = newsVideo ? 'video' : newsImage ? 'image' : 'text'

  const userHasReadRights = Boolean(news.permissions.self?.includes('read'))

  const tracking = shadow.useTracking()

  const setIsProjectDetailShareModalOpen = hooks.useSetModal('ProjectDetailShare')

  const auth = service.user.useAuth()

  const projectURLForNative = sharing.getSharingURL(
    'news_article',
    'native',
    project,
    auth.type === 'loggedin' ? auth.user : undefined,
  )

  return (
    <NewsArticle className={className}>
      {isEditMode && (
        <EditButtonWrapper href={`/projects/${project.id}/backoffice/news/${news.id}/`}>
          <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
        </EditButtonWrapper>
      )}
      {localNewsContentType === 'video' && newsVideo?.html && (
        <MediaWrapper>
          <VideoLoader
            isLocked={!userHasReadRights}
            videoHtml={newsVideo?.html ?? ''}
            consentWall={consent.media.Wall}
            placeholder={newsImage?.versions?.['large'].url ?? newsVideo?.thumbnail_url ?? ''}
          />
          <plume.styles.card.news.LockLabel asDesktop="L" isLocked={!userHasReadRights} type="video">
            <plume.glyphs.stroke.Lock size={21} />
            {getLockedMediaLabel()}
          </plume.styles.card.news.LockLabel>
        </MediaWrapper>
      )}
      {localNewsContentType === 'image' && newsImage && (
        <MediaWrapper>
          <plume.styles.card.news.LockLabel asDesktop="L" isLocked={!userHasReadRights} type="image">
            <plume.glyphs.stroke.Lock size={21} />
            {getLockedMediaLabel()}
          </plume.styles.card.news.LockLabel>
          <Picture alt={newsTitle} isLocked={!userHasReadRights} src={newsImage?.versions?.['full'].url ?? ''} />
        </MediaWrapper>
      )}
      <NewsHeader>
        <plume.styles.heading.M asDesktop="L" as="h1">
          {newsTitle}
        </plume.styles.heading.M>
        <plume.styles.card.news.NewsCardDate>{getNewsDate()}</plume.styles.card.news.NewsCardDate>
      </NewsHeader>

      {(newsContent || newsPreview) && (
        <NewsBody
          dangerouslySetInnerHTML={{ __html: (newsContent ?? newsPreview) as any }}
          isLocked={!userHasReadRights}
        />
      )}

      {!userHasReadRights && (
        <LockOverlay>
          <plume.styles.copy.M>
            <plume.glyphs.stroke.Lock size={16} />
            {getLockedContentLabel()}
          </plume.styles.copy.M>
          <LockedCTAWrapper>{lockedCTA}</LockedCTAWrapper>
        </LockOverlay>
      )}
      <plume.styles.card.news.NewsCardFooter>
        <plume.styles.card.news.CommentsContainer>
          <BubbleIcon size={16} />
          <plume.styles.card.news.CommentsCount as={plume.styles.copy.S}>
            {tnc('%(count)s comment', '%(count)s comments', news.comments_count, {
              count: news.comments_count,
            })}
          </plume.styles.card.news.CommentsCount>
        </plume.styles.card.news.CommentsContainer>
        {/* FIXME: Rename to `ProjectButton` */}
        <plume.ActionButton
          contents={{
            inactive: (
              <>
                <span>{t('Share')}</span>
                <plume.glyphs.stroke.Share size={17} />
              </>
            ),
            active: (
              <>
                <span>{t('Share')}</span>
                <plume.glyphs.stroke.Share size={17} />
              </>
            ),
          }}
          onClick={async () => {
            if (navigator.share && plume.utils.isMobile()) {
              await navigator.share({
                title: models.project.name(project),
                url: projectURLForNative,
              })

              tracking.social.track({
                eventCategory: shadow.CATEGORY.MEMBERSHIP_NEWS,
                eventLabel: 'news_article',
                socialAction: 'click: Share',
                socialNetwork: shadow.SOCIAL_NETWORK.NATIVE,
                socialTarget: project.absolute_url,
              })
            } else {
              setIsProjectDetailShareModalOpen(true)
            }
          }}
        />
      </plume.styles.card.news.NewsCardFooter>
    </NewsArticle>
  )

  function getNewsDate(): string | undefined {
    if (news.status === api.NewsStatus.ONLINE) {
      return dateUtils.format(dateUtils.parseISO(news.date_publication ?? news.date_creation), 'PPP')
    }

    if (news.status === api.NewsStatus.SCHEDULED && news.date_schedule) {
      const dateSchedule = dateUtils.parseISO(news.date_schedule)

      const yesterday = dateUtils.sub(new Date(), { days: 1 })
      const isDateScheduleAfterYesterday = dateUtils.isAfter(dateSchedule, yesterday)

      const formattedScheduledDate = isDateScheduleAfterYesterday
        ? dateUtils.format(dateSchedule, userEnv.lang === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
        : ` ${t('on')} ${dateUtils.format(dateSchedule, 'P')}`

      return t('Post scheduled for %(date)s at %(time)s', {
        date: formattedScheduledDate,
        time: dateUtils.format(dateSchedule, 'HH:mm'),
      })
    }

    if (news.status === api.NewsStatus.WAITING) {
      return t('Draft (only visible by you)')
    }
  }

  function getLockedMediaLabel() {
    if (news.audience_type === api.NewsAudienceType.SUPPORTERS) {
      const forAllSupporters = news.rewards === null || news.rewards.length === 0
      if (project.type === api.ProjectType.Membership) {
        return forAllSupporters ? t('For members only') : t('For selected members')
      }
      return forAllSupporters ? t('For backers only') : t('For selected backers')
    }
    if (news.audience_type === api.NewsAudienceType.PAYING) {
      return t('For paying members')
    }
    if (news.audience_type === api.NewsAudienceType.FANS) {
      return t('For fans only')
    }
    if (news.audience_type === api.NewsAudienceType.TIP) {
      return t('For selected backers')
    }
  }

  function getLockedContentLabel() {
    if (news.audience_type === api.NewsAudienceType.SUPPORTERS) {
      const forAllSupporters = news.rewards === null || news.rewards.length === 0
      if (project.type === api.ProjectType.Membership) {
        return forAllSupporters ? t('This post is for members only') : t('This post is for selected members only')
      }
      return forAllSupporters ? t('This post is for backers only') : t('This post is for selected backers only')
    }
    if (news.audience_type === api.NewsAudienceType.PAYING) {
      return t('This post is for paying members only')
    }
    if (news.audience_type === api.NewsAudienceType.FANS) {
      return t('This post is for fans only')
    }
    if (news.audience_type === api.NewsAudienceType.TIP) {
      return t('This post is for selected backers only')
    }
  }
}

const NewsArticle = styled.article`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  padding: 16px;
  position: relative;

  ${plume.EditButton} {
    position: absolute;
    right: 16px;
    top: 16px;
    z-index: 2;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 32px;
  }
`

const EditButtonWrapper = styled.a`
  position: absolute;
  right: 0;
  top: 0;
`

const MediaWrapper = styled.div`
  position: relative;
`

const VideoLoader = styled(plume.VideoLoader)`
  flex: none;
  width: 100%;
  height: fit-content;
  aspect-ratio: 16/9;
  border-radius: 4px;
  overflow: hidden;
`

const Picture = styled(plume.Picture)<{ isLocked: boolean }>`
  flex: none;
  border-radius: 4px;
  display: flex;

  ${(props) =>
    props.isLocked &&
    css`
      img {
        filter: blur(16px); // HACK - blur should be applied server side
      }

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${plume.COLORS.PRIMARY_GREY_900};
        opacity: 0.5;
      }
    `}
`

const NewsHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  word-wrap: break-word;
`

const NewsBody = styled(plume.WysiwygContent)<{ isLocked?: boolean }>`
  overflow: hidden;
  position: relative;

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ${({ isLocked = false }) =>
    isLocked &&
    css`
      -webkit-line-clamp: 5;
      max-height: 110px;

      &::after {
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        content: '';
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }
    `};
`

const LockOverlay = styled.div`
  align-content: center;
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;

  ${plume.styles.copy.M} {
    font-weight: 600; // HACK: Irregular font manipulation. We'll be adding copy.M.semiBold, so this is just by anticipation.
    text-align: center;

    ${plume.glyphs.Icon} {
      color: ${plume.COLORS.PRIMARY_SAND_500};
      margin-bottom: 4px;
      margin-right: 6px;
    }
  }
`

const BubbleIcon = styled(plume.glyphs.stroke.Bubble)`
  margin-top: 2px;
`

const LockedCTAWrapper = styled.div`
  display: flex;
  justify-content: center;
`
